/**
 * useDeliveryHistoryDetail Hook
 * 배달 상세 내역 조회 및 관리를 위한 커스텀 훅
 *
 * @description
 * - 배달 상세 내역 데이터 조회
 * - 라이더별 집계 뷰 / 상세 내역 뷰 전환
 * - 엑셀 다운로드 기능
 */
import { useState, useCallback, useEffect } from 'react';
import { deliveryHistoryDetailApi } from '../api/deliveryHistoryDetailApi';
import {
    DeliveryHistoryDetailFilters,
    DeliveryHistoryDetailResponse,
    DeliveryHistoryDetailRow,
    DeliveryHistoryDetailRawItem,
    DeliverySupplier,
    DeliveryRider,
    RiderSummary,
} from '../types/deliveryHistoryDetail.types';
import { getItem } from '@/shared/utils/storage';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { getDefaultDateRange } from '@/shared/utils/dateDefaultUtils';
import { toSafeAmount } from '@/shared/utils/apiResponseHelpers';

// ============================================================
// 상수 정의
// ============================================================

/** 필터 저장소 키 */
const FILTER_STORAGE_KEY = 'deliveryHistoryDetailFilters';

/** 세션 스토리지 키 */
const SESSION_PREFILL_KEY = 'deliveryHistoryDetailPrefill';
const SESSION_SOURCE_KEY = 'deliveryHistoryDetailPrefillSource';

/** 기본 필터 값 */
const BASE_FILTERS: DeliveryHistoryDetailFilters = {
    startDate: '',
    endDate: '',
    suppId: '',
    suppName: '',
    riderId: '',
};

// ============================================================
// 헬퍼 함수
// ============================================================

/**
 * 기본 필터 값 생성 (오늘 ~ 7일 전)
 */
const getDefaultFilters = (): DeliveryHistoryDetailFilters => {
    const { startDate, endDate } = getDefaultDateRange();
    return { ...BASE_FILTERS, startDate, endDate };
};

/**
 * API 응답을 정규화된 상세 행으로 변환
 */
const normalizeDetailRow = (item: DeliveryHistoryDetailRawItem): DeliveryHistoryDetailRow => ({
    suppId: item.suppId ?? '',
    riderId: item.riderId ?? '',
    suppName: item.suppName,
    riderName: item.riderName,
    deliveryDate: item.deliveryDate ?? '',
    deliveryNo: item.deliveryNo ?? '',
    deliveryStatus: item.deliveryStatus ?? '',
    serviceType: item.serviceType ?? '',
    deliveryType: item.deliveryType ?? '',
    deliveryMethod: item.deliveryMethod ?? '',
    shopNo: item.shopNo ?? '',
    shopName: item.shopName ?? '',
    goodsAmt: toSafeAmount(item.goodsAmt),
    pickupAddr: item.pickupAddr ?? '',
    deliveryAddr: item.deliveryAddr ?? '',
    orderTime: item.orderTime ?? '',
    assingTime: item.assingTime ?? '',
    shopArriTime: item.shopArriTime ?? '',
    pickupTime: item.pickupTime ?? '',
    deliveryTime: item.deliveryTime ?? '',
    distance: toSafeAmount(item.distance),
    pickupPlace: item.pickupPlace ?? '',
    baseAmt: toSafeAmount(item.baseAmt),
    weatherAmt: toSafeAmount(item.weatherAmt),
    addAmt: toSafeAmount(item.addAmt),
    peakTimeAmt: toSafeAmount(item.peakTimeAmt),
    locationAmt: toSafeAmount(item.locationAmt),
    deliveryAmt: toSafeAmount(item.deliveryAmt),
    riderFaultYn: item.riderFaultYn ?? '',
    addAmtReason: item.addAmtReason ?? '',
});

/**
 * 날짜 하이픈 제거 (YYYY-MM-DD → YYYYMMDD)
 */
const removeDateHyphens = (date: string): string => date.replace(/-/g, '');

// ============================================================
// Hook 정의
// ============================================================

export const useDeliveryHistoryDetail = () => {
    // 사용자 정보
    const userType = getItem<string>('USER_TYPE') || '';
    const userId = getItem<string>('USER_ID') || '';
    const isSupplierUser = userType === 'SUPPLIER' && !!userId;
    const { showToast } = useToast();

    // 필터 상태
    const [filters, setFilters] = useState<DeliveryHistoryDetailFilters>(() => {
        const saved = loadFilters<DeliveryHistoryDetailFilters>(FILTER_STORAGE_KEY);
        return saved || getDefaultFilters();
    });

    // 결과 상태
    const [results, setResults] = useState<DeliveryHistoryDetailRow[]>([]);
    const [riderSummaries, setRiderSummaries] = useState<RiderSummary[]>([]);
    const [summaryInfo, setSummaryInfo] = useState<{ totalDeliveryAmt?: string; totalDeliveryCount?: number }>({});
    const [isSummaryView, setIsSummaryView] = useState(false);

    // 로딩/에러 상태
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 선택 목록 상태
    const [suppliers, setSuppliers] = useState<DeliverySupplier[]>([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [riders, setRiders] = useState<DeliveryRider[]>([]);
    const [ridersLoading, setRidersLoading] = useState(false);

    // 기타 상태
    const [excelDownloading, setExcelDownloading] = useState(false);
    const [contextInfo, setContextInfo] = useState({
        suppId: '',
        suppName: '',
        riderId: '',
        riderName: '',
    });

    /**
     * 필터 값 변경
     */
    const handleFilterChange = useCallback(
        <K extends keyof DeliveryHistoryDetailFilters>(key: K, value: DeliveryHistoryDetailFilters[K]) => {
            setFilters((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    /**
     * 필터 초기화
     */
    const resetFilters = useCallback(() => {
        setFilters(getDefaultFilters());
    }, []);

    /**
     * 상세 내역 조회
     */
    const fetchDetails = useCallback(
        async (overrideFilters?: DeliveryHistoryDetailFilters) => {
            const requestFilters = overrideFilters ?? filters;

            if (!requestFilters.suppId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                setContextInfo({
                    suppId: requestFilters.suppId,
                    suppName: requestFilters.suppName ?? '',
                    riderId: requestFilters.riderId,
                    riderName: requestFilters.riderName ?? '',
                });

                const response: DeliveryHistoryDetailResponse =
                    await deliveryHistoryDetailApi.getDetails(requestFilters);

                // 라이더 미선택 시 집계 데이터 처리
                if (!requestFilters.riderId && !Array.isArray(response) && response?.riderSummaries) {
                    setIsSummaryView(true);
                    setRiderSummaries(response.riderSummaries);
                    setSummaryInfo({
                        totalDeliveryAmt: response.totalDeliveryAmt,
                        totalDeliveryCount: response.totalDeliveryCount,
                    });
                    setResults([]);
                    return;
                }

                // 라이더 선택 시 상세 데이터 처리
                setIsSummaryView(false);
                const items: DeliveryHistoryDetailRawItem[] = Array.isArray(response)
                    ? response
                    : (response?.items ?? []);

                const normalized = items.map(normalizeDetailRow);
                setResults(normalized);
                setRiderSummaries([]);
                setSummaryInfo({});

                // 컨텍스트 정보 업데이트
                if (normalized.length > 0) {
                    setContextInfo((prev) => ({
                        suppId: prev.suppId || normalized[0].suppId,
                        suppName: prev.suppName || normalized[0].suppName || '',
                        riderId: prev.riderId || normalized[0].riderId,
                        riderName: prev.riderName || normalized[0].riderName || '',
                    }));
                }
            } catch {
                setError('배달 내역 상세를 불러오는데 실패했습니다.');
                setResults([]);
                setRiderSummaries([]);
                setSummaryInfo({});
                setIsSummaryView(false);
            } finally {
                setLoading(false);
            }
        },
        [filters]
    );

    /**
     * 검색 폼 제출
     */
    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!filters.suppId) {
                showToast('협력사를 선택 후 검색 가능합니다.', { type: ToastType.WARNING });
                return;
            }

            fetchDetails();
            saveFilters(FILTER_STORAGE_KEY, filters);
        },
        [fetchDetails, filters, showToast]
    );

    /**
     * 협력사 목록 조회
     */
    const fetchSuppliers = useCallback(async () => {
        setSuppliersLoading(true);
        try {
            const list = await deliveryHistoryDetailApi.getSuppliers();
            setSuppliers(list);

            // 협력사 사용자는 자기 협력사로 고정
            if (isSupplierUser) {
                const matched = list.find((item) => item.suppId === userId);
                setFilters((prev) => {
                    if (prev.suppId && prev.suppId !== userId) return prev;
                    return {
                        ...prev,
                        suppId: userId,
                        suppName: matched?.suppName || prev.suppName || '',
                    };
                });
            } else {
                // 본사 사용자: suppId가 비어있으면 첫 번째 협력사를 기본 선택
                setFilters((prev) => {
                    if (prev.suppId || list.length === 0) return prev;
                    return {
                        ...prev,
                        suppId: list[0].suppId,
                        suppName: list[0].suppName || '',
                    };
                });
            }
        } catch {
            setSuppliers([]);
        } finally {
            setSuppliersLoading(false);
        }
    }, [isSupplierUser, userId]);

    /**
     * 라이더 목록 조회
     */
    const fetchRiders = useCallback(async (suppId: string) => {
        if (!suppId) {
            setRiders([]);
            return;
        }

        setRidersLoading(true);
        try {
            const list = await deliveryHistoryDetailApi.getRiders(suppId);
            setRiders(list);

            setFilters((prev) => {
                if (prev.suppId !== suppId) return prev;
                if (!prev.riderId) return { ...prev, riderName: '' };

                const matched = list.find((item) => item.riderId === prev.riderId);
                return matched ? { ...prev, riderName: matched.riderName } : { ...prev, riderId: '', riderName: '' };
            });
        } catch {
            setRiders([]);
            setFilters((prev) => ({ ...prev, riderId: '', riderName: '' }));
        } finally {
            setRidersLoading(false);
        }
    }, []);

    /**
     * 엑셀 다운로드
     */
    const handleExcelDownload = useCallback(async () => {
        if (!filters.startDate || !filters.endDate) {
            showToast('조회일자를 선택해주세요.', { type: ToastType.WARNING });
            return;
        }

        setExcelDownloading(true);
        try {
            const blob = await deliveryHistoryDetailApi.downloadExcel(filters);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            const startDate = removeDateHyphens(filters.startDate);
            const endDate = removeDateHyphens(filters.endDate);
            link.download = `${startDate}~${endDate}_배달상세내역.xlsx`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setExcelDownloading(false);
        }
    }, [filters, showToast]);

    // 초기 마운트: prefill 데이터 처리
    useEffect(() => {
        const source = sessionStorage.getItem(SESSION_SOURCE_KEY);
        const stored = sessionStorage.getItem(SESSION_PREFILL_KEY);

        if (source === 'history' && stored) {
            try {
                const parsed = JSON.parse(stored) as Partial<DeliveryHistoryDetailFilters>;
                const merged: DeliveryHistoryDetailFilters = { ...getDefaultFilters(), ...parsed };

                setContextInfo({
                    suppId: merged.suppId,
                    suppName: merged.suppName ?? '',
                    riderId: merged.riderId,
                    riderName: merged.riderName ?? '',
                });

                setFilters(merged);
                fetchDetails(merged);
            } catch {
                // prefill 파싱 실패 시 무시
            }
        } else {
            const savedFilters = loadFilters<DeliveryHistoryDetailFilters>(FILTER_STORAGE_KEY);
            if (savedFilters?.suppId) {
                fetchDetails(savedFilters);
            }
        }

        sessionStorage.removeItem(SESSION_PREFILL_KEY);
        sessionStorage.removeItem(SESSION_SOURCE_KEY);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 협력사 목록 조회
    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    // 협력사 변경 시 라이더 목록 조회
    useEffect(() => {
        fetchRiders(filters.suppId);
    }, [fetchRiders, filters.suppId]);

    return {
        filters,
        results,
        riderSummaries,
        summaryInfo,
        isSummaryView,
        loading,
        error,
        handleFilterChange,
        handleSubmit,
        resetFilters,
        refetch: fetchDetails,
        contextInfo,
        suppliers,
        suppliersLoading,
        riders,
        ridersLoading,
        isSupplierUser,
        excelDownloading,
        handleExcelDownload,
    };
};
